import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Target,
  Tag,
  Calendar,
  User,
  Building2,
  TrendingUp,
  Loader2,
  Plus,
  Users,
  ShoppingCart,
  Trash2,
  Package,
  AlertCircle,
  Percent,
  Edit,
  List,
  Check,
  X,
  Coins,
  Globe,
  Flag,
  DollarSign,
  UserCircle,
  Send,
  ArrowRightLeft,
  CalendarDays,
  FileText,
  UserCheck
} from "lucide-react";
import { FileUploader, Attachment } from "@/components/shared/FileUploader";
import { useToast } from "@/hooks/use-toast";
import { usePipelinePermissions } from "@/hooks/usePipelinePermissions";
import {
  opportunityAPI,
  Opportunity,
  OpportunityItem,
  AddItemWithoutRulesData,
  AddItemWithRulesData,
} from "@/services/opportunityService";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";
import { companyAPI } from "@/services/companyService";
import { contactoAPI, Contacto } from "@/services/contactoService";
import { OpportunityStage } from "@/services/opportunityService";
import { currencyAPI, Currency } from "@/services/currencyService";
import { CurrencyModal } from "./CurrencyModal";
import { usersService } from "@/services/usersService";
import type { User as SystemUser } from "@/types/crm";
import {
  productService,
  Product as ServiceProduct,
} from "@/services/product.service";
import { AddProductModal } from "./AddProductModal";
import { CreateRuleModal } from "@/components/products/CreateRuleModal";
import { Badge } from "@/components/ui/badge";
import { pipelineAPI } from "@/services/pipelineService";
import { useAuth } from "@/hooks/useAuth";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { CreateContactModal } from "@/components/contacts/CreateContactModal";
import { OpportunitySourcesModal } from "./OpportunitySourcesModal";

interface EditOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newId?: string) => void;
  slug: string;
  opportunity: Opportunity | null;
}

interface Product extends ServiceProduct {
  price?: number;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayValue = (value: string) => {
  if (!value) return "";
  const [integer, decimal] = value.split(".");
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal !== undefined
    ? `${formattedInteger}.${decimal}`
    : formattedInteger;
};

export function EditOpportunityModal({
  isOpen,
  onClose,
  onSuccess,
  slug,
  opportunity,
}: EditOpportunityModalProps) {
  const { toast } = useToast();
  const { user: authUser, company } = useAuth();
  const { canEditPipeline } = usePipelinePermissions();

  const [formData, setFormData] = useState({
    name: "",
    estimated_value: "0",
    success_probability: "50",
    stage_id: "",
    estimated_close_date: "",
    contact_id: "",
    client_company_id: "",
    opportunity_source_id: "",
    notes: "",
    priority: "Mediana",
    currency_id: "",
    created_by: "",
    lost_reason: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [discount, setDiscount] = useState({
    value: "0",
    type: "percentage" as "fixed" | "percentage",
  });
  const [selectedCollaboratorIds, setSelectedCollaboratorIds] = useState<
    string[]
  >([]);
  const [creationMode, setCreationMode] = useState<"simple" | "withProducts">(
    "simple",
  );
  const [selectedItemForRules, setSelectedItemForRules] = useState<any>(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);

  const [opportunityItems, setOpportunityItems] = useState<OpportunityItem[]>(
    [],
  );
  const [opportunityTotals, setOpportunityTotals] = useState({
    subtotal: 0,
    tax: 0,
    total: 0,
  });

  // --- Staging: items se editan localmente hasta que se presione Guardar ---
  type PendingAction =
    | { type: 'add'; data: any }
    | { type: 'update'; data: any }
    | { type: 'delete'; id: string };

  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);

  const [currentOpportunityId, setCurrentOpportunityId] = useState<string | null>(opportunity?.id || null);
  const [detailedOpportunity, setDetailedOpportunity] = useState<Opportunity | null>(null);

  const [contacts, setContacts] = useState<Contacto[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [stages, setStages] = useState<OpportunityStage[]>([]);
  const isLostStage = useMemo(() => {
    const stage = (stages as OpportunityStage[]).find(s => s.id === formData.stage_id);
    return stage?.stage_type === 'closed_lost';
  }, [formData.stage_id, stages]);
  
  const [sources, setSources] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [loading, setLoading] = useState({
    contacts: false,
    companies: false,
    stages: false,
    sources: false,
    currencies: false,
    users: false,
    products: false,
    items: false,
    submit: false,
  });

  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const currencySymbol = useMemo(() => {
    if ((company as any)?.currency) return (company as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  }, [company]);

  useEffect(() => {
    if (isOpen && slug) {
      fetchData();
    }
  }, [isOpen, slug, opportunity]);

  const fetchData = async () => {
    setIsLoadingAll(true);
    try {
      const stagesParams: any = {
        slug,
        is_active: true,
      };
      if (opportunity?.pipeline_id) {
        stagesParams.pipeline_id = opportunity.pipeline_id;
      }

      const [
        contactsData,
        companiesResponse,
        stagesResponse,
        sourcesResponse,
        currenciesResponse,
        usersResponse,
        productsResponse,
      ] = await Promise.all([
        contactoAPI.getContactos(slug),
        companyAPI.getCompanies({ slug }),
        opportunityAPI.getStages(stagesParams),
        opportunitySourcesAPI.getSources({ slug, is_active: true }),
        currencyAPI.getCurrencies({ slug, is_active: true }),
        usersService.getUsers(slug, 1, 1000, true),
        productService
          .getProducts({ slug, limit: 100 })
          .catch(() => ({ data: [] })),
      ]);

      setContacts(contactsData);
      setCompanies(companiesResponse.data);
      setStages(stagesResponse.data);
      setSources(sourcesResponse.data);
      setCurrencies(currenciesResponse.data);
      setProducts(productsResponse.data || []);
      setUsers(usersResponse.data || []);

      if (opportunity) {
        setDetailedOpportunity(opportunity);
        const getSafeId = (obj: any, idProp?: any) => {
          if (idProp && typeof idProp === "string" && idProp.length > 0)
            return idProp;
          if (obj && typeof obj === "object" && "id" in obj) return obj.id;
          if (typeof obj === "string" && obj.length > 0) return obj;
          return "";
        };

        const formatDateForInput = (dateString: string | null) => {
          if (!dateString) return "";
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "";
            return date.toISOString().split("T")[0];
          } catch (error) {
            return "";
          }
        };

        let cleanNotes = opportunity.notes || "";
        const discountMatch = opportunity.notes?.match(/\[DISCOUNT:({.*})\]/);
        if (discountMatch) {
          try {
            const parsed = JSON.parse(discountMatch[1]);
            setDiscount(parsed);
            cleanNotes = cleanNotes.replace(discountMatch[0], "").trim();
          } catch (e) {
            console.error("Error parsing discount", e);
          }
        }

        setFormData({
          name: opportunity.name || "",
          estimated_value: opportunity.estimated_value?.toString() || "0",
          success_probability:
            opportunity.success_probability?.toString() || "50",
          stage_id: getSafeId(opportunity.stage, opportunity.stage_id),
          estimated_close_date: formatDateForInput(
            opportunity.estimated_close_date,
          ),
          contact_id: getSafeId(
            opportunity.contact,
            (opportunity as any).contact_id,
          ),
          client_company_id: getSafeId(
            opportunity.client_company,
            (opportunity as any).client_company_id,
          ),
          opportunity_source_id: getSafeId(
            opportunity.opportunity_source,
            (opportunity as any).opportunity_source_id,
          ),
          notes: cleanNotes,
          priority: (opportunity as any).priority || "Mediana",
          currency_id: (opportunity as any).currency_id || "",
          created_by: (opportunity as any).created_by || "",
          lost_reason: opportunity.lost_reason || "",
        });

        setSelectedCollaboratorIds(
          opportunity.collaborators?.map((c) => c.user_id) || [],
        );
        const initialItems = opportunity.items || [];
        setOpportunityItems(initialItems);
        setPendingActions([]);
        setOpportunityTotals({
          subtotal: parseFloat(opportunity.subtotal?.toString() || "0"),
          tax: parseFloat(opportunity.tax?.toString() || "0"),
          total: parseFloat(opportunity.total?.toString() || "0"),
        });
        setCreationMode(
          initialItems.length > 0 ? "withProducts" : "simple",
        );
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setIsLoadingAll(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCurrencyInputChange = (field: string, value: string) => {
    const rawValue = value.replace(/[^0-9.]/g, "");
    handleChange(field, rawValue);
  };

  const handleDiscountChange = (value: string) => {
    const rawValue = value.replace(/[^0-9.]/g, "");
    setDiscount((prev) => ({ ...prev, value: rawValue }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = "El nombre es requerido";
    if (!formData.stage_id) newErrors.stage_id = "La etapa es requerida";
    if (!formData.contact_id) newErrors.contact_id = "El contacto es requerido";
    if (!formData.client_company_id)
      newErrors.client_company_id = "La empresa es requerida";
    if (!formData.currency_id) newErrors.currency_id = "La moneda es requerida";

    if (isLostStage && !formData.lost_reason) {
      newErrors.lost_reason = "El motivo de pérdida es requerido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setLoading((prev) => ({ ...prev, submit: true }));
    try {
      const finalNotes =
        discount.value !== "0"
          ? `${formData.notes}\n\n[DISCOUNT:${JSON.stringify(discount)}]`
          : formData.notes;

      const oppId = currentOpportunityId || opportunity?.id || "";

      // 1. Flush staged product actions first
      if (pendingActions.length > 0) {
        for (const action of pendingActions) {
          if (action.type === 'delete') {
            await opportunityAPI.deleteItem(action.id, slug, authUser);
          } else if (action.type === 'add') {
            await opportunityAPI.addItemWithRules({
              ...action.data,
              opportunity_id: oppId,
              slug,
              user: authUser,
            });
          } else if (action.type === 'update') {
            await opportunityAPI.updateItemWithRules({
              ...action.data,
              slug,
              user: authUser,
            });
          }
        }
        setPendingActions([]);
      }

      // 2. Save the main opportunity fields
      const dataToSave: any = {
        ...formData,
        success_probability: parseFloat(formData.success_probability) || 0,
        notes: finalNotes,
        slug,
        user: authUser,
        collaborators: selectedCollaboratorIds.map(id => ({ user_id: id })),
        estimated_value: parseFloat(formData.estimated_value) || 0,
      };

      if (oppId) {
        const response = await opportunityAPI.updateOpportunity(oppId, dataToSave);
        
        // Determinar el ID final (podría ser uno nuevo si se clonó la cotización)
        const finalId = response.data?.id || oppId;

        // 3. Upload attachments if any
        if (attachments && attachments.length > 0) {
          console.log(`Subiendo ${attachments.length} archivos para la cotización ${finalId}`);
          for (const att of attachments) {
            try {
              await opportunityAPI.uploadAttachment(finalId, slug, att.file, att.description || 'Archivo adjunto');
            } catch (err) {
              console.error("Error al subir archivo adjunto:", err);
              toast({
                title: "Error parcial",
                description: `No se pudo subir el archivo ${att.file.name}`,
                variant: "destructive"
              });
            }
          }
        }

        if (finalId !== oppId) {
          onSuccess(finalId);
        } else {
          onSuccess();
        }
      }

      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la cotización",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  // --- Handlers that only mutate local state (staging) ---
  const handleAddOrUpdateProduct = (productData: any) => {
    const isEdit = isEditingExisting && productData.id;

    // Compute a lightweight line_total for display
    const basePrice = parseFloat(productData.base_price) || 0;
    const qty = productData.quantity || 1;
    const baseSubtotal = basePrice * qty;
    const rulesTotal = (productData.applied_rules || []).reduce((acc: number, r: any) => {
      const modifier = parseFloat(r.price_modifier) || 0;
      if (r.price_type === "percentage") {
        return acc + baseSubtotal * (modifier / 100);
      } else if (r.price_type === "per_unit") {
        return acc + modifier * qty;
      } else {
        // fixed: suma una sola vez sin importar la cantidad
        return acc + modifier;
      }
    }, 0);
    const lineTotal = baseSubtotal + rulesTotal;

    if (isEdit) {
      // Find existing item to preserve original_price if possible
      const existingItem = opportunityItems.find(i => i.id === productData.id);
      const catalogPrice = parseFloat(
        existingItem?.original_price?.toString() || 
        existingItem?.product?.base_price?.toString() || 
        basePrice.toString()
      );
      const hasDeviation = Math.abs(basePrice - catalogPrice) > 0.01;

      // Stage an update
      setPendingActions(prev => [
        ...prev.filter(a => !(a.type === 'update' && a.data.id === productData.id)
                          && !(a.type === 'add' && a.data._localId === productData.id)),
        { type: 'update', data: { ...productData, has_deviation: hasDeviation } }
      ]);
      setOpportunityItems(prev =>
        prev.map(item =>
          item.id === productData.id
            ? { ...item, ...productData, has_deviation: hasDeviation, line_total: lineTotal.toFixed(2) }
            : item
        )
      );
    } else {
      // Stage an add with a temp local id
      const tempId = `_new_${Date.now()}`;
      
      const catalogProduct = products.find(p => p.id === productData.product_id);
      const catalogPrice = catalogProduct ? (parseFloat(catalogProduct.base_price?.toString()) || 0) : basePrice;
      const hasDeviation = Math.abs(basePrice - catalogPrice) > 0.01;

      const newItem: any = {
        id: tempId,
        _isNew: true,
        product_id: productData.product_id || null,
        description: productData.description || productData.manualName || '',
        product: catalogProduct || null,
        quantity: qty,
        base_price: basePrice.toFixed(2),
        original_price: catalogPrice.toFixed(2),
        has_deviation: hasDeviation,
        applied_rules: productData.applied_rules || [],
        line_total: lineTotal.toFixed(2),
      };
      setPendingActions(prev => [...prev, { type: 'add', data: { ...productData, _localId: tempId, has_deviation: hasDeviation } }]);
      setOpportunityItems(prev => [...prev, newItem]);
    }

    // Recalculate subtotal display
    setOpportunityTotals(prev => {
      const allItems = isEdit
        ? opportunityItems.map(i => i.id === productData.id ? { ...i, line_total: lineTotal.toFixed(2) } : i)
        : [...opportunityItems, { line_total: lineTotal.toFixed(2) } as any];
      const sub = allItems.reduce((s, i) => s + parseFloat(i.line_total?.toString() || '0'), 0);
      return { ...prev, subtotal: sub, total: sub + prev.tax };
    });

    setFormData(prev => ({
      ...prev,
      estimated_value: (lineTotal + opportunityItems
        .filter(i => i.id !== productData.id)
        .reduce((s, i) => s + parseFloat(i.line_total?.toString() || '0'), 0)).toFixed(2)
    }));

    setShowAddProductModal(false);
    setIsEditingExisting(false);
  };

  const handleDeleteItem = (itemId: string) => {
    const isNew = itemId.startsWith('_new_');
    if (isNew) {
      setPendingActions(prev => prev.filter(a => !(a.type === 'add' && a.data._localId === itemId)));
    } else {
      setPendingActions(prev => [
        ...prev.filter(a => !(a.type === 'update' && a.data.id === itemId)),
        { type: 'delete', id: itemId }
      ]);
    }
    setOpportunityItems(prev => prev.filter(i => i.id !== itemId));
    setOpportunityTotals(prev => {
      const remaining = opportunityItems.filter(i => i.id !== itemId);
      const sub = remaining.reduce((s, i) => s + parseFloat(i.line_total?.toString() || '0'), 0);
      return { ...prev, subtotal: sub, total: sub + prev.tax };
    });
  };

  const getUserDisplayName = (user: SystemUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user.nombre || user.name || "";
  };

  const calculateTotalWithDiscount = () => {
    const currentTotal = opportunityTotals.subtotal + opportunityTotals.tax;
    let discountAmount = 0;
    const discountVal = parseFloat(discount.value) || 0;

    if (discount.type === "percentage") {
      discountAmount = (currentTotal * discountVal) / 100;
    } else {
      discountAmount = discountVal;
    }

    return Math.max(0, currentTotal - discountAmount);
  };

  const getDetailedTotals = () => {
    let baseSubtotal = 0;
    let extrasSubtotal = 0;
    
    opportunityItems.forEach((item: any) => {
      const qty = item.quantity || 1;
      const numericBasePrice = parseFloat(item.base_price?.toString() || "0");
      const itemBaseTotal = numericBasePrice * qty;
      baseSubtotal += itemBaseTotal;
      
      const lineTotal = parseFloat(item.line_total?.toString() || "0");
      extrasSubtotal += (lineTotal - itemBaseTotal);
    });

    const finalTotal = calculateTotalWithDiscount();
    const currentTotal = opportunityTotals.subtotal + opportunityTotals.tax;
    const discountAmount = Math.max(0, currentTotal - finalTotal);

    return {
      baseSubtotal,
      extrasSubtotal,
      subtotal: baseSubtotal + extrasSubtotal,
      tax: opportunityTotals.tax,
      discountAmount,
      finalTotal
    };
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <div className="flex flex-col h-full max-h-[90vh]">
            <DialogHeader className="p-6 pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <DialogTitle className="text-xl font-bold flex items-center gap-2">
                    <Edit className="h-5 w-5 text-blue-600" />
                    Editar Cotización: {detailedOpportunity?.name || opportunity?.name}
                  </DialogTitle>
                  <DialogDescription>
                    Versión V{detailedOpportunity?.version_number || opportunity?.version_number}.{detailedOpportunity?.minor_version ?? opportunity?.minor_version ?? 0} · ID: #{(detailedOpportunity?.id || opportunity?.id)?.substring(0, 8).toUpperCase()}
                  </DialogDescription>
                </div>
                {!opportunity?.is_latest_version && (
                   <Badge variant="destructive" className="uppercase text-[10px]">
                    Historial / Lectura
                   </Badge>
                )}
              </div>

              {/* Toggle Mode - Match Create Style */}
              <div className="flex bg-slate-100 p-1.5 rounded-2xl w-fit mt-6 shadow-inner border border-slate-200 mx-auto">
                <button
                  type="button"
                  onClick={() => setCreationMode("simple")}
                  className={`flex items-center px-8 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    creationMode === "simple"
                      ? "bg-white text-blue-600 shadow-md ring-1 ring-slate-200"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Target className="h-4 w-4 mr-2" />
                  Básica
                </button>
                <button
                  type="button"
                  onClick={() => setCreationMode("withProducts")}
                  className={`flex items-center px-8 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    creationMode === "withProducts"
                      ? "bg-white text-blue-600 shadow-md ring-1 ring-slate-200"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Productos
                </button>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-6 pt-2 custom-scrollbar">
              {isLoadingAll ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
                  <p className="text-sm text-slate-500">Cargando datos...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Form Grid - Only in Simple Mode */}
                  {creationMode === "simple" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="name" className="flex items-center">
                          <Target className="h-4 w-4 mr-2" />
                          Nombre de la Cotización *
                        </Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          placeholder="Nombre de la cotización"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="estimated_value" className="flex items-center">
                          <Coins className="h-4 w-4 mr-2" />
                          Valor Estimado
                        </Label>
                        <div className="relative mt-1">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <span className="text-gray-500 font-medium">{currencySymbol}</span>
                          </div>
                          <Input
                            id="estimated_value"
                            value={formatDisplayValue(formData.estimated_value)}
                            onChange={(e) => handleCurrencyInputChange("estimated_value", e.target.value)}
                            className="pl-10"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="success_probability" className="flex items-center">
                          <TrendingUp className="h-4 w-4 mr-2" />
                          Probabilidad (%)
                        </Label>
                        <Select
                          onValueChange={(val) => handleChange("success_probability", val)}
                          value={formData.success_probability}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10% - Muy baja</SelectItem>
                            <SelectItem value="25">25% - Baja</SelectItem>
                            <SelectItem value="50">50% - Media</SelectItem>
                            <SelectItem value="75">75% - Alta</SelectItem>
                            <SelectItem value="90">90% - Muy alta</SelectItem>
                            <SelectItem value="100">100% - Cerrada ganada</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="stage_id" className="flex items-center">
                          <List className="h-4 w-4 mr-2" />
                          Etapa *
                        </Label>
                        <Select
                          value={formData.stage_id}
                          onValueChange={(val) => handleChange("stage_id", val)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleccionar etapa" />
                          </SelectTrigger>
                          <SelectContent>
                            {stages.map((stage) => (
                              <SelectItem key={stage.id} value={stage.id}>
                                <div className="flex items-center gap-2">
                                  {stage.color && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />}
                                  {stage.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="estimated_close_date" className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          Cierre Estimado
                        </Label>
                        <Input
                          type="date"
                          id="estimated_close_date"
                          value={formData.estimated_close_date}
                          onChange={(e) => handleChange("estimated_close_date", e.target.value)}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact_id" className="flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          Contacto *
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Select
                            value={formData.contact_id}
                            onValueChange={(val) => handleChange("contact_id", val)}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Contacto" />
                            </SelectTrigger>
                            <SelectContent>
                              {contacts.map((contact) => (
                                <SelectItem key={contact.id} value={contact.id}>
                                  {contact.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="outline" size="icon" onClick={() => setShowContactModal(true)}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="client_company_id" className="flex items-center">
                          <Building2 className="h-4 w-4 mr-2" />
                          Empresa *
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Select
                            value={formData.client_company_id}
                            onValueChange={(val) => handleChange("client_company_id", val)}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Empresa" />
                            </SelectTrigger>
                            <SelectContent>
                              {companies.map((company) => (
                                <SelectItem key={company.id} value={company.id}>
                                  {company.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="outline" size="icon" onClick={() => setShowCompanyModal(true)}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="opportunity_source_id" className="flex items-center">
                          <Globe className="h-4 w-4 mr-2" />
                          Fuente
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Select
                            value={formData.opportunity_source_id}
                            onValueChange={(val) => handleChange("opportunity_source_id", val)}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Fuente" />
                            </SelectTrigger>
                            <SelectContent>
                              {sources.map((source) => (
                                <SelectItem key={source.id} value={source.id}>
                                  {source.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="outline" size="icon" onClick={() => setShowSourceModal(true)}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label className="flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          Colaboradores
                        </Label>
                        <Select onValueChange={(val) => {
                          if (!selectedCollaboratorIds.includes(val)) {
                            setSelectedCollaboratorIds(prev => [...prev, val]);
                          }
                        }}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Añadir..." />
                          </SelectTrigger>
                          <SelectContent>
                            {users.filter(u => !selectedCollaboratorIds.includes(u.id) && u.id !== authUser?.id).map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {getUserDisplayName(u)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedCollaboratorIds.map(id => {
                            const u = users.find(user => user.id === id);
                            return (
                              <Badge key={id} variant="secondary" className="gap-1 pr-1">
                                {u ? getUserDisplayName(u) : id}
                                <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedCollaboratorIds(prev => prev.filter(uid => uid !== id))} />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <Label htmlFor="notes" className="flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          Notas / Próximo Paso
                        </Label>
                        <Input
                          id="notes"
                          value={formData.notes}
                          onChange={(e) => handleChange("notes", e.target.value)}
                          placeholder="Detalles adicionales..."
                          className="mt-1"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <FileUploader
                          attachments={attachments}
                          onAttachmentsChange={setAttachments}
                          maxFiles={5}
                          label="Adjuntos"
                        />
                      </div>
                    </div>
                  )}

                  {/* Lost Reason Section - Only in Simple Mode */}
                  {isLostStage && creationMode === "simple" && (
                    <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                      <Label className="text-rose-800 font-bold mb-2 block">Motivo de pérdida</Label>
                      <Textarea
                        value={formData.lost_reason}
                        onChange={(e) => handleChange("lost_reason", e.target.value)}
                        placeholder="¿Por qué se perdió esta cotización?"
                        className="bg-white border-rose-200"
                      />
                    </div>
                  )}

                  {/* Products Section - Only if creationMode is withProducts */}
                  {creationMode === "withProducts" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">Listado de Productos</h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsEditingExisting(false);
                            setShowAddProductModal(true);
                          }}
                          className="bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Añadir Producto
                        </Button>
                      </div>

                      {opportunityItems.length === 0 ? (
                        <div className="text-center py-10 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                          <ShoppingCart className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs text-slate-400">No hay productos en esta cotización</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {opportunityItems.map((item: any) => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg group hover:border-blue-300 transition-all">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-100 rounded-md">
                                  <Package className="h-4 w-4 text-slate-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold">{item.product?.name || item.description}</p>
                                  <div className="mt-1 w-full">
                                    {(() => {
                                      const catalogPrice = item.product ? parseFloat(item.original_price?.toString() || item.product.base_price?.toString() || "0") : parseFloat(item.base_price?.toString() || "0");
                                      const numericBasePrice = parseFloat(item.base_price?.toString() || "0");
                                      const isModified = item.product && (item.has_deviation || Math.abs(numericBasePrice - catalogPrice) > 0.01);
                                      
                                      const baseSubtotal = numericBasePrice * item.quantity;
                                      const lineTotal = parseFloat(item.line_total?.toString() || "0");
                                      const extrasTotal = lineTotal - baseSubtotal;
                                      const rulesCount = item.applied_rules?.length || 0;

                                      return (
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                                          <span>
                                            <span className="font-bold text-slate-700">Cant:</span> {item.quantity}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <span className="font-bold text-slate-700">P. Base:</span> 
                                            {isModified && (
                                              <span className="line-through text-slate-400">{currencySymbol}{formatCurrency(catalogPrice)}</span>
                                            )}
                                            <span className={`${isModified ? "text-orange-600 font-bold" : ""}`}>
                                              {currencySymbol}{formatCurrency(numericBasePrice)}
                                              {isModified && " (Modif.)"}
                                            </span>
                                          </span>
                                          {rulesCount > 0 && (
                                            <span className="text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 flex items-center">
                                              +{rulesCount} Ajustes (+{currencySymbol}{formatCurrency(extrasTotal)})
                                            </span>
                                          )}
                                          <span className="font-bold text-slate-800 ml-auto bg-slate-100 px-2 py-0.5 rounded">
                                            Total: {currencySymbol}{formatCurrency(lineTotal)}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-blue-600" onClick={() => {
                                  setSelectedItemForRules(item);
                                  setIsEditingExisting(true);
                                  setShowAddProductModal(true);
                                }}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-rose-600" onClick={() => handleDeleteItem(item.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Summary & Discount Card - Same as Create but with Totals from State */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex flex-col md:flex-row justify-between gap-6">
                           <div className="space-y-3">
                              <Label className="text-xs font-bold text-slate-500 uppercase">Descuento Global</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  value={formatDisplayValue(discount.value)}
                                  onChange={(e) => handleDiscountChange(e.target.value)}
                                  className="w-24 h-9 bg-white"
                                  placeholder="0.00"
                                />
                                <div className="flex bg-white rounded-md p-0.5 border border-slate-200">
                                  <button
                                    type="button"
                                    onClick={() => setDiscount({ ...discount, type: "percentage" })}
                                    className={`p-1.5 rounded-md ${discount.type === "percentage" ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                                  >
                                    <Percent className="h-3 w-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setDiscount({ ...discount, type: "fixed" })}
                                    className={`p-1.5 rounded-md ${discount.type === "fixed" ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                                  >
                                    <Tag className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                           </div>

                           <div className="w-full md:w-64 space-y-2">
                              {(() => {
                                const totals = getDetailedTotals();
                                return (
                                  <>
                                    <div className="space-y-1.5 pb-3 border-b border-slate-200 mb-2 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Resumen de Ítems</p>
                                      {opportunityItems.map((item: any, i: number) => {
                                        const lineTotal = parseFloat(item.line_total?.toString() || "0");
                                        const name = item.product?.name || item.description || "Producto";
                                        return (
                                          <div key={i} className="flex justify-between items-start text-[11px] text-slate-600 gap-2">
                                            <span className="truncate flex-1" title={name}>{item.quantity}x {name}</span>
                                            <span className="font-medium">{currencySymbol}{formatCurrency(lineTotal)}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                                      <span>Subtotal</span>
                                      <span>{currencySymbol}{formatCurrency(totals.subtotal)}</span>
                                    </div>
                                    {totals.discountAmount > 0 && (
                                      <div className="flex justify-between items-center text-xs text-red-600">
                                        <span>Descuento Global</span>
                                        <span>-{currencySymbol}{formatCurrency(totals.discountAmount)}</span>
                                      </div>
                                    )}
                                    {totals.tax > 0 && (
                                      <div className="flex justify-between items-center text-xs text-slate-500">
                                        <span>IVA / Impuestos</span>
                                        <span>{currencySymbol}{formatCurrency(totals.tax)}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-2">
                                      <span className="text-sm font-bold text-slate-900">Total Neto</span>
                                      <span className="text-xl font-bold text-blue-700">
                                        {formatCurrency(totals.finalTotal)}
                                      </span>
                                    </div>
                                  </>
                                );
                              })()}
                           </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="p-6 border-t border-slate-100 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={loading.submit}>
                Cancelar
              </Button>
              <Button 
                onClick={() => handleSubmit()} 
                disabled={loading.submit} 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              >
                {loading.submit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <CurrencyModal
        isOpen={showCurrencyModal}
        onClose={() => setShowCurrencyModal(false)}
        slug={slug}
      />
      
      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        allProducts={products}
        slug={slug}
        onRefreshProducts={() => {
          productService.getProducts({ slug, limit: 100 }).then(res => {
            setProducts(res.data || []);
          });
        }}
        onAddProduct={handleAddOrUpdateProduct}
        initialData={isEditingExisting && selectedItemForRules ? {
          id: selectedItemForRules.id,
          product_id: selectedItemForRules.product?.id || "",
          quantity: selectedItemForRules.quantity || 1,
          base_price: parseFloat(selectedItemForRules.base_price) || 0,
          applied_rules: selectedItemForRules.applied_rules?.map((r: any) => {
            const ruleObj = r.rule || {};
            return { 
              rule_id: r.rule_id, 
              rule_name: r.rule_name || ruleObj.rule_name,
              is_custom: r.is_custom,
              price_modifier: r.price_modifier || ruleObj.price_modifier || r.calculated_amount,
              price_type: r.price_type || ruleObj.price_type,
              description: r.description || ""
            };
          }) || [],
        } : null}
      />

      <CreateCompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        companySlug={slug}
        onSave={async (newCompanyData) => {
          try {
            const response = await companyAPI.createCompany(newCompanyData);
            if (response.data) {
              setCompanies(prev => [...prev, response.data]);
              setFormData(prev => ({ ...prev, client_company_id: response.data.id }));
            }
          } catch (error) { console.error(error); }
        }}
      />

      <CreateContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSave={async (newContact) => {
          try {
            const response = await contactoAPI.createContacto(slug, newContact);
            setContacts(prev => [...prev, response]);
            setFormData(prev => ({ ...prev, contact_id: response.id }));
          } catch (error) { console.error(error); }
        }}
      />

      <OpportunitySourcesModal
        isOpen={showSourceModal}
        onClose={() => setShowSourceModal(false)}
        slug={slug}
        onSourceCreated={fetchData}
      />
    </>
  );
}
